import { useState } from 'react';
import { useList, useApiUrl } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { Plus, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TagPickerWithCreate } from '../../tags/components';
import type { TagItem } from '../../tags/types';
import type { UserTag } from '../types';

interface UserTagsProps {
  userId: number;
  tags: UserTag[];
}

/**
 * Секция управления тегами пользователя на странице show.
 * Показывает текущие теги с кнопкой удаления и форму добавления нового тега.
 * Мутации идут напрямую через fetch (не через Refine useCreate/useDelete),
 * так как эндпоинт вложенный (/users/:id/tags/:tagId).
 */
export function UserTags({ userId, tags }: UserTagsProps) {
  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removingTagId, setRemovingTagId] = useState<number | null>(null);

  // Загружаем все активные теги для выбора
  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { mode: 'off' },
    queryOptions: { staleTime: 60_000 },
  });
  const availableTags = tagsResult.result.data ?? [];

  // Фильтруем теги, которые ещё не назначены пользователю
  const assignedTagIds = new Set(tags.map((t) => t.id));
  const unassignedTags = availableTags.filter((t) => !assignedTagIds.has(t.id));

  const invalidateUser = () => {
    void queryClient.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey;
        return (
          Array.isArray(key) &&
          key.some(
            (part) =>
              typeof part === 'string' &&
              (part.includes(`/users/${userId}`) || part === 'users'),
          )
        );
      },
    });
  };

  const addTag = async (tagId: number) => {
    setIsAdding(true);
    try {
      const response = await fetch(`${apiUrl}/users/${userId}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tagId }),
      });
      if (!response.ok) {
        const json = await response.json().catch(() => null);
        const message: string =
          (json as { errors?: string[] } | null)?.errors?.[0] ?? 'Не удалось добавить тег';
        notify.error(message);
        return;
      }
      notify.success('Тег добавлен');
      setSelectedTagId(null);
      invalidateUser();
    } catch {
      notify.error('Ошибка сети при добавлении тега');
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddTag = async () => {
    if (selectedTagId === null) { return; }
    await addTag(selectedTagId);
  };

  const handleRemoveTag = async (tagId: number) => {
    setRemovingTagId(tagId);
    try {
      const response = await fetch(`${apiUrl}/users/${userId}/tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const json = await response.json().catch(() => null);
        const message: string =
          (json as { errors?: string[] } | null)?.errors?.[0] ?? 'Не удалось снять тег';
        notify.error(message);
        return;
      }
      notify.success('Тег снят');
      invalidateUser();
    } catch {
      notify.error('Ошибка сети при снятии тега');
    } finally {
      setRemovingTagId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Текущие теги */}
      {tags.length === 0 ? (
        <p className="text-sm text-muted-foreground">Теги не назначены</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium text-white"
              style={{ backgroundColor: tag.color }}
            >
              <span>{tag.name}</span>
              <button
                type="button"
                aria-label={`Снять тег ${tag.name}`}
                disabled={removingTagId === tag.id}
                onClick={() => { void handleRemoveTag(tag.id); }}
                className="ml-0.5 rounded-full p-0.5 hover:bg-black/20 disabled:opacity-50 transition-colors"
              >
                {removingTagId === tag.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Форма добавления тега */}
      <div className="flex items-center gap-2">
        <div className="w-[260px]">
          <TagPickerWithCreate
            tags={unassignedTags}
            value={selectedTagId}
            onChange={(tagId) => setSelectedTagId(tagId)}
            onCreated={(tag) => { void addTag(tag.id); }}
            allowClear={false}
            placeholder="Выбрать тег"
            disabled={isAdding}
            createDialogTitle="Создать и назначить тег"
            createDialogDescription="Новый тег сразу будет назначен этому пользователю."
            createSuccessMessage="Тег создан"
          />
        </div>
        {unassignedTags.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            disabled={selectedTagId === null || isAdding}
            onClick={() => { void handleAddTag(); }}
          >
            {isAdding ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Добавить
          </Button>
        )}
      </div>

      {unassignedTags.length === 0 && availableTags.length > 0 && tags.length > 0 && (
        <Badge variant="outline" className="text-xs text-muted-foreground">
          Все доступные теги уже назначены
        </Badge>
      )}

      {tagsResult.query.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка тегов…
        </div>
      )}
    </div>
  );
}
